/**
 * Pure rules behind the employer-branding page.
 *
 * Mirrors app/utils/branding.py on the backend, so the editor can say "too
 * light" or "not a video we can show" before the save rather than after it.
 * The API remains the authority and re-checks both.
 */

/** White text sits on the accent colour, so it has to clear WCAG AA. */
export const MINIMUM_ACCENT_CONTRAST = 4.5;

export const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

export const ACCENT_TOO_LIGHT =
  'This colour is too light for white text on top of it. Pick a darker shade.';

function relativeLuminance(hex) {
  const [red, green, blue] = [1, 3, 5].map((index) => {
    const srgb = parseInt(hex.slice(index, index + 2), 16) / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

/** WCAG contrast ratio between `hex` and white, 1 to 21; null when not `#rrggbb`. */
export function contrastWithWhite(hex) {
  if (typeof hex !== 'string' || !HEX_COLOR.test(hex.trim())) return null;
  return 1.05 / (relativeLuminance(hex.trim()) + 0.05);
}

/** True when white text on `hex` is readable. */
export function isReadableAccent(hex) {
  const ratio = contrastWithWhite(hex);
  return ratio !== null && ratio >= MINIMUM_ACCENT_CONTRAST;
}

/**
 * Inline style that sets `--accent` for a subtree.
 *
 * The accent is company-supplied data, so it only ever reaches the page as a
 * CSS custom property, never as a class name. Anything that is not a readable
 * `#rrggbb` is dropped and the var() fallback -- the app's brand colour --
 * applies instead.
 */
export function accentStyle(hex) {
  return isReadableAccent(hex) ? { '--accent': hex.trim().toLowerCase() } : undefined;
}

/** The accent with the brand fallback, for use inside a `style` value. */
export const ACCENT_VAR = 'var(--accent, var(--color-brand-600))';

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const VIMEO_ID = /^[0-9]{6,12}$/;
const YOUTUBE_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com']);
const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com', 'player.vimeo.com']);

/**
 * Recognise a YouTube or Vimeo link to a single video; null for anything else.
 *
 * Only answers "will the API accept this". The page never builds an embed
 * URL from it -- it iframes the `embed_url` the API returns.
 */
export function parseVideoUrl(value) {
  let parsed;
  try {
    parsed = new URL(String(value).trim());
  } catch {
    return null;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  const host = parsed.hostname.toLowerCase();
  const segments = parsed.pathname.split('/').filter(Boolean);

  if (YOUTUBE_HOSTS.has(host)) {
    let candidate = '';
    if (parsed.pathname === '/watch') {
      candidate = parsed.searchParams.get('v') ?? '';
    } else if (segments.length === 2 && ['embed', 'shorts', 'live'].includes(segments[0])) {
      candidate = segments[1];
    } else {
      return null;
    }
    return YOUTUBE_ID.test(candidate) ? { provider: 'youtube', id: candidate } : null;
  }

  if (host === 'youtu.be') {
    const candidate = segments.length === 1 ? segments[0] : '';
    return YOUTUBE_ID.test(candidate) ? { provider: 'youtube', id: candidate } : null;
  }

  if (VIMEO_HOSTS.has(host)) {
    if (host === 'player.vimeo.com' && segments[0] !== 'video') return null;
    const candidate = segments.at(-1) ?? '';
    return VIMEO_ID.test(candidate) ? { provider: 'vimeo', id: candidate } : null;
  }

  return null;
}

/**
 * A thin ring in the accent around a logo tile -- the quiet way a listing
 * carries a company's colour. Falls back to the pale brand tint, which is
 * what an unbranded tile already had.
 */
export const ACCENT_RING = {
  boxShadow: '0 0 0 2px var(--color-surface), 0 0 0 3.5px var(--accent, var(--color-brand-100))',
};
